// Procedural PIXI unit sprite — direct PIXI classes (NOT @pixi/react JSX).
// We use PIXI imperatively because the playback engine needs imperative
// control (tween via refs) and the unit "tree" is small enough that
// JSX adds friction without ergonomic upside.
//
// One CombatUnit = one Container that owns:
//   - body (rounded rect, tribe-tinted border)
//   - portrait (radial bg + 2-letter initials in faction font)
//   - tape strip (rotated rectangle, tribe color)
//   - tier chip (top-left)
//   - attack badge (bottom-left, aerosol red)
//   - hp badge (bottom-right, glow-stick green)
//   - shield ring (only when divineShield flag set)
//
// All state changes go through public mutators (setHp, setAtk, setDead,
// setShield, pulse). External code never reaches in to mutate the
// child graphics directly — that's the contract this module promises.

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Card } from '../game/data/cards/types';
import { getTribeDefinition } from '../game/data/tribes';

// Card dimensions — match v1 feel; tuned for 7-wide row at ~360px viewport
export const UNIT_W = 78;
export const UNIT_H = 104;
const PORTRAIT_INSET = 4;
const PORTRAIT_H = 70;

const COLOR_ASPHALT = 0x1a1612;
const COLOR_STUCCO_1 = 0x2a221c;
const COLOR_STUCCO_3 = 0x3d3128;
const COLOR_VOID = 0x130f0a;
const COLOR_BONE = 0xf5ecd6;
const COLOR_SHARPIE = 0x141008;
const COLOR_GRAPHITE = 0x8a7e6c;
const COLOR_AEROSOL_RED = 0xe6342a;
const COLOR_GLOW_STICK = 0x5fd17e;
const COLOR_SODIUM = 0xff8a3d;
const COLOR_CHOLO = 0xff5a1f;
const COLOR_EMO = 0xff2e7e;

function getInitials(name: string): string {
  const parts = name.split(/[\s-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function factionFontFamily(faction: Card['faction']): string {
  if (faction === 'cholo') return 'Pirata One, UnifrakturCook, Times New Roman, serif';
  if (faction === 'emo') return 'Permanent Marker, Special Elite, Courier New, monospace';
  return 'Pirata One, UnifrakturCook, serif';
}

function factionAccentColor(faction: Card['faction']): number {
  if (faction === 'cholo') return COLOR_CHOLO;
  if (faction === 'emo') return COLOR_EMO;
  return COLOR_GRAPHITE;
}

export class CombatUnit extends Container {
  readonly card: Card;
  readonly side: 'player' | 'enemy';
  readonly instanceId: string;
  /** Home position in board-row coordinates. Lunges spring back to here. */
  homeX: number;
  homeY: number;

  private body: Graphics;
  private portraitBg: Graphics;
  private tape: Graphics;
  private initialsText: Text;
  private tierText: Text;
  private atkText: Text;
  private hpText: Text;
  private atkBadge: Graphics;
  private hpBadge: Graphics;
  private shieldRing: Graphics;
  private deadOverlay: Graphics;

  private currentHp: number;
  private currentAtk: number;
  private hasShield: boolean;
  private dead: boolean = false;

  constructor(card: Card, side: 'player' | 'enemy', homeX: number, homeY: number) {
    super();
    this.card = card;
    this.side = side;
    this.instanceId = card.instanceId ?? `${card.id}_${Math.random()}`;
    this.homeX = homeX;
    this.homeY = homeY;
    this.x = homeX;
    this.y = homeY;
    // Pivot at card center so scale/lunge feels natural.
    this.pivot.set(UNIT_W / 2, UNIT_H / 2);

    this.currentHp = card.health;
    this.currentAtk = card.attack;
    this.hasShield = !!card.flags?.divineShield;

    const tribe = card.tribe ? getTribeDefinition(card.tribe) : undefined;
    const tribeColor = tribe ? tribe.color : factionAccentColor(card.faction);

    // --- body (card frame) ---
    this.body = new Graphics();
    this.drawBody(tribeColor);
    this.addChild(this.body);

    // --- portrait area ---
    this.portraitBg = new Graphics();
    this.portraitBg.rect(
      PORTRAIT_INSET,
      PORTRAIT_INSET,
      UNIT_W - PORTRAIT_INSET * 2,
      PORTRAIT_H,
    );
    this.portraitBg.fill({ color: COLOR_VOID });
    this.addChild(this.portraitBg);

    // --- tape strip ---
    this.tape = new Graphics();
    this.tape.rect(-22, -4, 44, 8);
    this.tape.fill({ color: tribeColor, alpha: 0.7 });
    this.tape.x = 38;
    this.tape.y = 8;
    this.tape.rotation = -0.05;
    this.addChild(this.tape);

    // --- initials ---
    this.initialsText = new Text({
      text: getInitials(card.name),
      style: new TextStyle({
        fontFamily: factionFontFamily(card.faction),
        fontSize: 48,
        fill: tribeColor,
        align: 'center',
      }),
    });
    this.initialsText.anchor.set(0.5);
    this.initialsText.x = UNIT_W / 2;
    this.initialsText.y = PORTRAIT_INSET + PORTRAIT_H / 2;
    this.addChild(this.initialsText);

    // --- tier chip ---
    const tierBg = new Graphics();
    tierBg.circle(0, 0, 9);
    tierBg.fill({ color: COLOR_STUCCO_3 });
    tierBg.x = 12;
    tierBg.y = 12;
    this.addChild(tierBg);

    this.tierText = new Text({
      text: String(card.tier),
      style: new TextStyle({
        fontFamily: 'JetBrains Mono, Consolas, monospace',
        fontSize: 11,
        fill: COLOR_BONE,
      }),
    });
    this.tierText.anchor.set(0.5);
    this.tierText.x = 12;
    this.tierText.y = 12;
    this.addChild(this.tierText);

    // --- atk badge ---
    this.atkBadge = new Graphics();
    this.atkBadge.roundRect(0, 0, 22, 22, 2);
    this.atkBadge.fill({ color: COLOR_AEROSOL_RED });
    this.atkBadge.x = 2;
    this.atkBadge.y = PORTRAIT_INSET + PORTRAIT_H - 14;
    this.addChild(this.atkBadge);

    this.atkText = new Text({
      text: String(card.attack),
      style: new TextStyle({
        fontFamily: 'JetBrains Mono, Consolas, monospace',
        fontSize: 14,
        fontWeight: 'bold',
        fill: COLOR_BONE,
      }),
    });
    this.atkText.anchor.set(0.5);
    this.atkText.x = this.atkBadge.x + 11;
    this.atkText.y = this.atkBadge.y + 11;
    this.addChild(this.atkText);

    // --- hp badge ---
    this.hpBadge = new Graphics();
    this.hpBadge.roundRect(0, 0, 22, 22, 2);
    this.hpBadge.fill({ color: COLOR_GLOW_STICK });
    this.hpBadge.x = UNIT_W - 24;
    this.hpBadge.y = PORTRAIT_INSET + PORTRAIT_H - 14;
    this.addChild(this.hpBadge);

    this.hpText = new Text({
      text: String(card.health),
      style: new TextStyle({
        fontFamily: 'JetBrains Mono, Consolas, monospace',
        fontSize: 14,
        fontWeight: 'bold',
        fill: COLOR_SHARPIE,
      }),
    });
    this.hpText.anchor.set(0.5);
    this.hpText.x = this.hpBadge.x + 11;
    this.hpText.y = this.hpBadge.y + 11;
    this.addChild(this.hpText);

    // --- name text ---
    const nameText = new Text({
      text: card.name.length > 12 ? card.name.slice(0, 11) + '…' : card.name,
      style: new TextStyle({
        fontFamily: factionFontFamily(card.faction),
        fontSize: 12,
        fill: card.faction === 'neutral' ? 0xc8b89a : tribeColor,
        align: 'center',
      }),
    });
    nameText.anchor.set(0.5, 0);
    nameText.x = UNIT_W / 2;
    nameText.y = PORTRAIT_INSET + PORTRAIT_H + 4;
    this.addChild(nameText);

    // --- shield ring (lazy: drawn but invisible if no shield) ---
    this.shieldRing = new Graphics();
    this.shieldRing.circle(UNIT_W / 2, PORTRAIT_INSET + PORTRAIT_H / 2, 38);
    this.shieldRing.stroke({ color: 0x9ec8d8, width: 2.5, alpha: 0.85 });
    this.shieldRing.visible = this.hasShield;
    this.addChild(this.shieldRing);

    // --- dead overlay (final fade-out tint) ---
    this.deadOverlay = new Graphics();
    this.deadOverlay.rect(0, 0, UNIT_W, UNIT_H);
    this.deadOverlay.fill({ color: COLOR_ASPHALT, alpha: 0 });
    this.deadOverlay.eventMode = 'none';
    this.addChild(this.deadOverlay);
  }

  private drawBody(borderColor: number) {
    this.body.clear();
    this.body.roundRect(0, 0, UNIT_W, UNIT_H, 6);
    this.body.fill({ color: COLOR_STUCCO_1 });
    this.body.stroke({ color: borderColor, width: 1.5, alpha: 0.6 });
  }

  // --- public mutators (the only outside-world API surface) ---

  setHp(value: number) {
    this.currentHp = Math.max(0, value);
    this.hpText.text = String(this.currentHp);
  }

  setAtk(value: number) {
    this.currentAtk = Math.max(0, value);
    this.atkText.text = String(this.currentAtk);
  }

  getHp() {
    return this.currentHp;
  }

  getAtk() {
    return this.currentAtk;
  }

  setShield(active: boolean) {
    this.hasShield = active;
    this.shieldRing.visible = active;
  }

  hasDivineShield() {
    return this.hasShield;
  }

  isDead() {
    return this.dead;
  }

  markDead() {
    this.dead = true;
  }

  /** Apply a snapshot alpha for a fade — used by death tween. */
  setFadeAlpha(a: number) {
    this.alpha = Math.max(0, Math.min(1, a));
  }

  /** Glow the attack badge briefly (helper for permanent_buff feedback). */
  flashAtk() {
    this.atkBadge.tint = 0xffffff;
    setTimeout(() => {
      this.atkBadge.tint = 0xffffff;
    }, 0);
  }

  /** Recolor stat badge using a sodium tint, briefly. */
  glowBadges() {
    this.atkBadge.tint = COLOR_SODIUM;
    this.hpBadge.tint = COLOR_SODIUM;
    setTimeout(() => {
      this.atkBadge.tint = 0xffffff;
      this.hpBadge.tint = 0xffffff;
    }, 160);
  }
}
